import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../../shared/shared.module';
import { CanvasService } from '../../../services/canvas.service';
import { I18nService } from '../../../i18n/i18n.service';

export interface SceneItem {
  id: number;
  textValue: string;
  pictures: { url: string }[];
  loading: boolean;
  isUploading: boolean;
  canvasContext?: CanvasRenderingContext2D | null;
  isDrawing: boolean;
  lastX: number;
  lastY: number;
}

@Component({
  selector: 'app-storyboard-scene',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './storyboard-scene.component.html',
  styleUrls: ['./storyboard-scene.component.scss']
})
export class StoryboardSceneComponent implements AfterViewInit {
  @ViewChild('drawCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  
  @Input() scene!: SceneItem;
  @Input() sceneIndex!: number;
  @Input() updatedImageUrl: string | null = null;
  
  @Output() textChange = new EventEmitter<{ value: string; index: number }>();
  @Output() expressEdit = new EventEmitter<number>();
  @Output() canvasReady = new EventEmitter<{ context: CanvasRenderingContext2D; index: number }>();

  constructor(private canvasService: CanvasService, public i18n: I18nService) {}

  ngAfterViewInit(): void {
    if (this.canvasRef && this.canvasRef.nativeElement) {
      const context = this.canvasService.initializeCanvas(this.canvasRef);
      if (context) {
        this.scene.canvasContext = context;
        this.canvasReady.emit({ context, index: this.sceneIndex });
      }
    }
  }

  onMouseDown(event: MouseEvent): void {
    this.canvasService.onMouseDown(event, this.scene);
  }

  onMouseMove(event: MouseEvent): void {
    this.canvasService.onMouseMove(event, this.scene);
  }

  onMouseUp(): void {
    this.canvasService.onMouseUp(this.scene);
  }

  onTouchStart(event: TouchEvent): void {
    event.preventDefault();
    this.canvasService.onTouchStart(event, this.scene);
  }

  onTouchMove(event: TouchEvent): void {
    event.preventDefault();
    this.canvasService.onTouchMove(event, this.scene);
  }

  onTouchEnd(event: TouchEvent): void {
    event.preventDefault();
    this.canvasService.onTouchEnd(this.scene);
  }

  onTextChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.textChange.emit({ value: target.value, index: this.sceneIndex });
  }

  openExpressEditor(): void {
    this.expressEdit.emit(this.scene.id);
  }

  get displayImageUrl(): string {
    return this.updatedImageUrl || this.scene.pictures[0]?.url || '';
  }

  get hasImage(): boolean {
    return !!this.scene.pictures[0]?.url;
  }
}